import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Users, UserPlus, Check, X, Loader2, Heart, Shield, Trash2, Search
} from "lucide-react";
import {
  getAccountabilityPartners, sendAccountabilityRequest,
  acceptAccountability, declineAccountability, removeAccountability
} from "@/lib/api";
import { toast } from "sonner";
import api from "@/lib/api";

function PartnerCard({ partnership, onAccept, onDecline, onRemove }) {
  const { partner, status, is_requester, partner_progress, pair_id } = partnership;
  const pct = partner_progress && partner_progress.total > 0
    ? Math.round((partner_progress.completed / partner_progress.total) * 100) : 0;

  return (
    <Card className="border-[#E5E0D5] bg-white" data-testid={`partner-card-${pair_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F0EBE0] flex items-center justify-center flex-shrink-0 overflow-hidden">
            {partner.profile_photo ? (
              <img src={partner.profile_photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-['Nunito'] text-sm font-bold text-[#6B665E]">{(partner.name || "?")[0]}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-['Nunito'] text-sm font-medium text-[#2D2A26] truncate">{partner.name}</p>
            {status === "active" && partner_progress && (
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-1.5 rounded-full bg-[#F0EBE0] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: "var(--liturgical-primary)" }} />
                </div>
                <span className="font-['Nunito'] text-xs font-medium" style={{ color: "var(--liturgical-primary)" }}>
                  {partner_progress.completed}/{partner_progress.total}
                </span>
              </div>
            )}
            {status === "pending" && (
              <p className="font-['Nunito'] text-xs text-amber-600 mt-0.5">
                {is_requester ? "Awaiting their response" : "Wants to be your partner"}
              </p>
            )}
          </div>

          {status === "pending" && !is_requester && (
            <div className="flex gap-1.5 flex-shrink-0">
              <Button size="icon" onClick={() => onAccept(pair_id)} className="w-8 h-8 bg-emerald-500 hover:bg-emerald-600" data-testid={`accept-partner-${pair_id}`}>
                <Check className="w-4 h-4 text-white" />
              </Button>
              <Button size="icon" variant="outline" onClick={() => onDecline(pair_id)} className="w-8 h-8 border-red-200 text-red-500 hover:bg-red-50" data-testid={`decline-partner-${pair_id}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          {status === "active" && (
            <Button size="icon" variant="ghost" onClick={() => onRemove(pair_id)} className="w-8 h-8 text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0" data-testid={`remove-partner-${pair_id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AccountabilityTab() {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadPartners = useCallback(async () => {
    try {
      const res = await getAccountabilityPartners();
      setPartnerships(res.data.partnerships || []);
    } catch (err) {
      console.error("Failed to load partners:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.get('/users/search', { params: { query: searchQuery, limit: 10 } });
      setSearchResults(res.data.users || []);
    } catch {
      toast.error("Search failed");
    }
    setSearching(false);
  };

  const handleSendRequest = async (userId) => {
    try {
      const res = await sendAccountabilityRequest(userId);
      if (res.data.status === "active") {
        toast.success(res.data.message || "Partnership activated!");
      } else {
        toast.success("Request sent! They'll need to accept.");
      }
      setShowSearch(false);
      setSearchQuery("");
      setSearchResults([]);
      await loadPartners();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send request");
    }
  };

  const handleAccept = async (pairId) => {
    try {
      await acceptAccountability(pairId);
      toast.success("Partnership activated!");
      await loadPartners();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    }
  };

  const handleDecline = async (pairId) => {
    try {
      await declineAccountability(pairId);
      toast.success("Request declined");
      await loadPartners();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to decline");
    }
  };

  const handleRemove = async (pairId) => {
    try {
      await removeAccountability(pairId);
      toast.success("Partnership removed");
      await loadPartners();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  const activePartners = partnerships.filter(p => p.status === "active");
  const pendingReceived = partnerships.filter(p => p.status === "pending" && !p.is_requester);
  const pendingSent = partnerships.filter(p => p.status === "pending" && p.is_requester);
  const partnerIds = partnerships.map(p => p.partner.user_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--liturgical-primary)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="accountability-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">Accountability Partners</h2>
          <p className="font-['Nunito'] text-xs text-[#6B665E]">Support each other in building holy habits</p>
        </div>
        <Button size="sm" onClick={() => setShowSearch(true)} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-xs h-8" data-testid="add-partner-btn">
          <UserPlus className="w-3 h-3 mr-1" /> Add Partner
        </Button>
      </div>

      {/* Pending Requests Received */}
      {pendingReceived.length > 0 && (
        <div>
          <h3 className="font-['Nunito'] text-sm font-semibold text-amber-600 mb-2">Pending Requests</h3>
          <div className="space-y-2">
            {pendingReceived.map(p => (
              <PartnerCard key={p.pair_id} partnership={p} onAccept={handleAccept} onDecline={handleDecline} onRemove={handleRemove} />
            ))}
          </div>
        </div>
      )}

      {/* Active Partners */}
      {activePartners.length > 0 && (
        <div>
          <h3 className="font-['Nunito'] text-sm font-semibold text-[#2D2A26] mb-2">Active Partners</h3>
          <div className="space-y-2">
            {activePartners.map(p => (
              <PartnerCard key={p.pair_id} partnership={p} onAccept={handleAccept} onDecline={handleDecline} onRemove={handleRemove} />
            ))}
          </div>
        </div>
      )}

      {/* Pending Sent */}
      {pendingSent.length > 0 && (
        <div>
          <h3 className="font-['Nunito'] text-sm font-semibold text-[#9B968E] mb-2">Sent Requests</h3>
          <div className="space-y-2">
            {pendingSent.map(p => (
              <PartnerCard key={p.pair_id} partnership={p} onAccept={handleAccept} onDecline={handleDecline} onRemove={handleRemove} />
            ))}
          </div>
        </div>
      )}

      {partnerships.length === 0 && (
        <Card className="border-[#E5E0D5] bg-white">
          <CardContent className="p-8 text-center">
            <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: "var(--liturgical-primary)" }} />
            <p className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-1">Find an Accountability Partner</p>
            <p className="font-['Nunito'] text-sm text-[#6B665E] mb-4">Invite a friend to share your spiritual journey. You'll see each other's daily progress.</p>
            <Button onClick={() => setShowSearch(true)} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="find-partner-btn">
              <UserPlus className="w-4 h-4 mr-1.5" /> Find a Partner
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Search / Add Partner Dialog */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
              <UserPlus className="w-5 h-5" style={{ color: "var(--liturgical-primary)" }} /> Find a Partner
            </DialogTitle>
            <DialogDescription className="font-['Nunito'] text-sm text-[#6B665E]">
              Search for a member to invite as your accountability partner.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by name..."
                className="flex-1 border-[#E5E0D5] font-['Nunito']"
                data-testid="partner-search-input"
              />
              <Button onClick={handleSearch} disabled={searching} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white" data-testid="partner-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            <ScrollArea className="max-h-64">
              <div className="space-y-1">
                {searchResults.map(u => (
                  <div key={u.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F9F7F2]">
                    <div className="w-8 h-8 rounded-full bg-[#F0EBE0] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {u.profile_photo ? (
                        <img src={u.profile_photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="font-['Nunito'] text-xs font-bold text-[#6B665E]">{(u.name || "?")[0]}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-['Nunito'] text-sm font-medium text-[#2D2A26] truncate">{u.name}</p>
                    </div>
                    {partnerIds.includes(u.user_id) ? (
                      <span className="font-['Nunito'] text-xs text-[#9B968E]">Already connected</span>
                    ) : (
                      <Button size="sm" onClick={() => handleSendRequest(u.user_id)} className="h-7 px-2 text-xs bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid={`invite-partner-${u.user_id}`}>
                        <Heart className="w-3 h-3 mr-1" /> Invite
                      </Button>
                    )}
                  </div>
                ))}
                {searchResults.length === 0 && searchQuery && !searching && (
                  <p className="text-center font-['Nunito'] text-sm text-[#6B665E] py-4">No members found. Try a different name.</p>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
